import { useMemo, useState } from 'react'
import Switch from '@mui/material/Switch'
import { Box, Button, Chip, Link, Typography } from '@mui/material'
import VideocamIcon from '@mui/icons-material/Videocam'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import NoDataImg from '../../../assets/img/no-data.gif'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import urls from '../../constants/apiurl'
import ApiCaller from '../../constants/ApiCaller'
import { v4 as uuidv4 } from 'uuid'
import { showDangerToast, showToast } from '../../constants/toaster'
import Utils from '../../constants/Utils'
import { PlayVideoInModal } from '../../custom/PlayVideoInModal'

const label = { inputProps: { 'aria-label': 'Active status' } }

const VIDEO_EXT = /\.(mp4|webm|mov|m3u8|avi|mkv)(\?.*)?$/i
const isVideoUrl = (url) => {
  if (!url || typeof url !== 'string') return false
  const lower = url.toLowerCase()
  return VIDEO_EXT.test(lower) || lower.includes('/video/') || lower.includes('video=true')
}

const isLikelyImageUrl = (url) => url && typeof url === 'string' && !isVideoUrl(url)

const getVideoLink = (row) => {
  const candidates = [row?.link, row?.videoLink, row?.videoUrl]
  return candidates.find((u) => u && isVideoUrl(u)) || candidates.find(Boolean) || ''
}

const getVideoThumbnail = (row) => {
  const candidates = [
    row?.thumbnail,
    row?.thumbnailUrl,
    row?.videoThumbnail,
    row?.thumbNail,
    row?.imageUrl,
    row?.poster,
    row?.posterUrl
  ]
  return candidates.find(isLikelyImageUrl) || ''
}

const getUserProfileId = (row) => {
  const candidates = [row?.profileLink, row?.userProfileId, row?.profileId]
  for (const id of candidates) {
    if (id != null && id !== '' && !isVideoUrl(String(id))) return id
  }
  if (row?.link != null && row.link !== '' && !isVideoUrl(String(row.link))) return row.link
  return row?.userId
}

const VideoThumbnailPreview = ({ row, onPlay }) => {
  const videoLink = getVideoLink(row)
  const thumb = getVideoThumbnail(row)
  const [imgFailed, setImgFailed] = useState(false)

  if (!videoLink) {
    return (
      <Typography variant='caption' color='text.secondary'>
        No video
      </Typography>
    )
  }

  const showVideoFrame = !thumb || imgFailed

  return (
    <Box
      onClick={onPlay}
      sx={{
        position: 'relative',
        width: 96,
        height: 64,
        borderRadius: 1,
        overflow: 'hidden',
        cursor: 'pointer',
        bgcolor: '#2d3748',
        border: '1px solid #e9ecef',
        '&:hover .video-play-overlay': {
          bgcolor: 'rgba(0,0,0,0.45)',
        },
      }}
      role='button'
      aria-label='Play video'
    >
      {showVideoFrame ? (
        <Box
          component='video'
          src={videoLink}
          muted
          playsInline
          preload='metadata'
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            pointerEvents: 'none',
            bgcolor: '#2d3748',
          }}
          onLoadedMetadata={(e) => {
            try {
              e.currentTarget.currentTime = 0.25
            } catch {
              /* ignore */
            }
          }}
        />
      ) : (
        <Box
          component='img'
          src={thumb}
          alt={`Video by ${row?.userName || 'user'}`}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            bgcolor: '#edf2f7',
          }}
          onError={() => setImgFailed(true)}
        />
      )}

      <Box
        className='video-play-overlay'
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'rgba(0,0,0,0.2)',
          transition: 'background-color 0.2s',
          pointerEvents: 'none',
        }}
      >
        <PlayCircleOutlineIcon sx={{ color: '#fff', fontSize: 32, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.6))' }} />
      </Box>
    </Box>
  )
}

export const VideoAbuseTable = ({
  datalist,
  setShowAbuseReportDetails,
  pagerecord,
  setCallAPi,
  activePermission = true
}) => {
  const navigate = useNavigate()
  const userData = useSelector((state) => state?.userData)
  const { formatDate } = Utils()

  const [showPlayVideoModal, setShowPlayVideoModal] = useState(false)
  const [videoSrc, setVideoSrc] = useState('')
  const [activeVideoIndex, setActiveVideoIndex] = useState(0)

  const playableVideos = useMemo(
    () => (datalist || []).filter((row) => getVideoLink(row)),
    [datalist]
  )

  const previewVideo = playableVideos[activeVideoIndex] || null

  const openVideoAtIndex = (index) => {
    const list = playableVideos
    if (index < 0 || index >= list.length) return
    setActiveVideoIndex(index)
    setVideoSrc(getVideoLink(list[index]))
    setShowPlayVideoModal(true)
  }

  const handleVideoIndexChange = (index) => {
    const list = playableVideos
    if (index < 0 || index >= list.length) return
    setActiveVideoIndex(index)
    setVideoSrc(getVideoLink(list[index]))
  }

  const openVideoFromRow = (row) => {
    const idx = playableVideos.findIndex(
      (v) => v?.typeId === row?.typeId || getVideoLink(v) === getVideoLink(row)
    )
    if (idx >= 0) openVideoAtIndex(idx)
    else if (getVideoLink(row)) {
      setActiveVideoIndex(0)
      setVideoSrc(getVideoLink(row))
      setShowPlayVideoModal(true)
    }
  }

  const goToUserManager = (row) => {
    const profileId = getUserProfileId(row)
    if (profileId != null && profileId !== '') {
      navigate(`/user-manager/${profileId}`)
    }
  }

  const handleStatusChange = async (val) => {
    try {
      const path = `${urls.activeInActiveReport}?video=true&id=${val?.typeId}`
      const res = await ApiCaller.get({}, path, userData?.access_token)
      const response = await res.response.json()
      if (response.status == 200) {
        showToast(response?.message)
        setCallAPi(uuidv4())
      } else {
        showDangerToast(response.message)
      }
    } catch (error) {
      console.error(error)
      showDangerToast(error.message)
    }
  }

  const openReportsFromModal = (row) => {
    setShowPlayVideoModal(false)
    setVideoSrc('')
    setShowAbuseReportDetails({ ...row, reportKind: 'VIDEO' })
  }

  if (!datalist?.length) {
    return (
      <Box className='text-center' py={5}>
        <img width='22%' src={NoDataImg} alt='No data' style={{ maxWidth: 140 }} />
        <Typography fontWeight={700} sx={{ mt: 2 }}>
          No video reports found
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          Try adjusting filters or date range
        </Typography>
      </Box>
    )
  }

  const startIndex = (pagerecord.pageNumber - 1) * pagerecord.pageSize

  return (
    <Box>
      <Box display='flex' alignItems='center' gap={1} mb={2}>
        <VideocamIcon sx={{ color: '#fb6340' }} fontSize='small' />
        <Typography variant='subtitle2' fontWeight={700} color='text.secondary'>
          VIDEO REPORTS
        </Typography>
        <Typography variant='caption' color='text.secondary' sx={{ ml: 1 }}>
          Click thumbnail to play · username opens user profile
        </Typography>
      </Box>

      <div className='table-responsive'>
        <table className='table align-items-center table-flush'>
          <thead className='thead-light'>
            <tr>
              <th scope='col'>#</th>
              <th scope='col'>User ID</th>
              <th scope='col'>User name</th>
              <th scope='col' style={{ minWidth: 120 }}>
                Video preview
              </th>
              <th scope='col'>Reports</th>
              <th scope='col'>Last report</th>
              <th scope='col'>Active</th>
              <th scope='col'>Actions</th>
            </tr>
          </thead>
          <tbody>
            {datalist.map((val, ind) => {
              const profileId = getUserProfileId(val)
              return (
                <tr key={val?.typeId || val?.userId || ind}>
                  <td>{startIndex + ind + 1}</td>
                  <td>
                    {profileId ? (
                      <Link
                        component='button'
                        variant='body2'
                        underline='hover'
                        sx={{ wordBreak: 'break-all', fontWeight: 600, textAlign: 'left' }}
                        onClick={() => goToUserManager(val)}
                      >
                        {val?.userId || '—'}
                      </Link>
                    ) : (
                      <Typography variant='body2' sx={{ wordBreak: 'break-all' }}>
                        {val?.userId || '—'}
                      </Typography>
                    )}
                  </td>
                  <td>
                    {profileId ? (
                      <Link
                        component='button'
                        variant='body1'
                        underline='hover'
                        fontWeight={600}
                        onClick={() => goToUserManager(val)}
                      >
                        {val?.userName || '—'}
                      </Link>
                    ) : (
                      <Typography fontWeight={600}>{val?.userName || '—'}</Typography>
                    )}
                  </td>
                  <td>
                    <VideoThumbnailPreview row={val} onPlay={() => openVideoFromRow(val)} />
                  </td>
                  <td>
                    <Chip label={val?.reportCount ?? 0} color='error' size='small' variant='outlined' />
                  </td>
                  <td>{formatDate(val?.updatedAt)}</td>
                  <td>
                    <Switch
                      {...label}
                      checked={!!val?.active}
                      disabled={!activePermission}
                      onChange={() => {
                        if (activePermission) handleStatusChange(val)
                      }}
                      size='small'
                    />
                  </td>
                  <td>
                    <Button
                      size='small'
                      variant='outlined'
                      onClick={() => setShowAbuseReportDetails({ ...val, reportKind: 'VIDEO' })}
                    >
                      View reports
                    </Button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <PlayVideoInModal
        videoSrc={videoSrc}
        show={showPlayVideoModal}
        onHide={() => {
          setShowPlayVideoModal(false)
          setVideoSrc('')
        }}
        videos={playableVideos}
        currentIndex={activeVideoIndex}
        onIndexChange={handleVideoIndexChange}
        getVideoUrl={getVideoLink}
        getVideoLabel={(video) =>
          video?.userName ? `@${video.userName}` : video?.caption || 'Reported video'
        }
        previewVideo={previewVideo}
        activePermission={activePermission}
        onViewUser={(row) => {
          setShowPlayVideoModal(false)
          goToUserManager(row)
        }}
        viewUserLabel='Open user manager'
        onViewReports={openReportsFromModal}
        viewReportsLabel='View reports'
        onStatusChange={(e, _id) => {
          if (previewVideo && activePermission) {
            handleStatusChange(previewVideo)
          }
        }}
      />
    </Box>
  )
}
